import { useEffect, useState } from "react";

export const isValidEmail = (email) => {
    if (typeof email !== "string") {
      return false;
    }
    // This regex is a widely-used pattern for email validation.
    // It checks for a valid structure and allows for characters like '+' in the local part.
    // It is anchored at the start (^) and end ($) and only allows one '@',
    // making it a reliable check for a single, well-formed email address.
    // The 'i' flag makes the validation case-insensitive.
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
    return emailRegex.test(email) && email.split("@").length === 2;
};

export function isNotBlank(value) {
    return !!value?.replace(/\s/g, '').length;
}

export function isCPSemail(value) {
    // Check if the email is a valid CPS email
    const isValid = isValidEmail(value);
    if (!isValid) {
        return false;
    }else{
        // Check if the email ends with @cps.edu and ensure there are no trailing spaces
        return value.trim().toLowerCase().endsWith("@cps.edu");
    }
}

export function isDelete(value) {
    return value === "delete";
}

export function isNotSelected(value){
    return !!value
}

// accepts a string and checks to make sure that it contains at least 8 characters
export function is8OrMoreCharacters(value) {
    return value?.length >= 8;
}

// accepts a string and checks to make sure that it contains no longer than characters
export function isNotLongerthan140(value) {
    return value?.length <= 140;
}

// accepts a string and checks to make sure it has at least one capital letter, one lowercase letter, one number, and one symbol
export function isStrongPassword(value) {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(value);
}

// accepts a string and checks for at least one capital letter
export function hasCapitalLetter(value) {
    return /[A-Z]/.test(value);
}

// accepts a string and checks for at least one lowercase letter
export function hasLowercaseLetter(value) {
    return /[a-z]/.test(value);
}

// accepts a string and checks for at least one number
export function hasNumber(value) {
    return /[0-9]/.test(value);
}

//function to check if the string is a number
export function isNumber(value) {
    return !isNaN(value) && value.trim() !== "";
}

// accepts a string and checks for at least one symbol
export function hasSymbol(value) {
    return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
}

//accepts new name and old name and return true if they are not the same
export function isNotWithSameNameInAccount(value, oldVal) {
    return value?.toLowerCase() !== oldVal?.toLowerCase()
}

export function isShorterOrEqualTo14(value){
    return value?.length <= 14;
}

// ***** USE VALIDATION *****
// --INPUT-- accepts an object of validations to track:
/*
Example
    {
        "name": { // a name given to the component/input to identify it
            isNotBlank: true // a preset validation to apply to it, do not include if not needed
        },
        "email": {
            isValidEmail: true
        }
    }
*/

// --OUTPUT--returns an object of errors and a validate function
/*
Example of errors object
    {
        "name": { // the name given to the component/input to identify it
            isValid: false, // whether or not the input is valid
            errorMessages: {
                isNotBlank: "Can not be blank"
            }
        },
        "email": {
            isValid: true,
            errorMessages: {}
        }
    }
*/
export function useValidation(validations) {
    const [errors, setErrors] = useState({});

    const errorConfigs = {
      // each callback must accept a value and return true or false; true is valid, false is invalid
      isNotBlank: {
        callback: isNotBlank,
        message: "Cannot be blank.",
      },
      isShorterOrEqualTo14: {
        callback: isShorterOrEqualTo14,
        message: "Title must be 14 characters or fewer",
      },
      isNotWithSameNameInAccount: {
        callback: isNotWithSameNameInAccount,
        message: "Dashboard name already taken. Try another.",
      },
      isNotLongerthan140: {
        callback: isNotLongerthan140,
        message: "Longer than 140 characters.",
      },
      isNotSelected: {
        callback: isNotSelected,
        message: "No value selected.",
      },
      isValidEmail: {
        callback: isValidEmail,
        message: "Email is not valid.",
      },
      isCPSemail: {
        callback: isCPSemail,
        message: "Must be cps.edu email",
      },
      isDelete: {
        callback: isDelete,
        message: `Please type the word 'delete' exactly as shown.`,
      },
      is8OrMoreCharacters: {
        callback: is8OrMoreCharacters,
        message: "Must be at least 8 characters.",
      },
      isStrongPassword: {
        callback: isStrongPassword,
        message:
          "Must contain at least one uppercase letter, one lowercase letter, one number, and one symbol.",
      },
      hasCapitalLetter: {
        callback: hasCapitalLetter,
        message: "Must contain at least one capital letter.",
      },
      hasLowercaseLetter: {
        callback: hasLowercaseLetter,
        message: "Must contain at least one lowercase letter.",
      },
      hasNumber: {
        callback: hasNumber,
        message: "Must contain at least one number.",
      },
      isNumber: {
        callback: isNumber,
        message: "Must be a valid number.",
      },
      hasSymbol: {
        callback: hasSymbol,
        message: "Must contain at least one symbol.",
      },
    };

    useEffect(()=>{
        const errorObj = {};
    
        Object.keys(validations).forEach(inputName => {
            errorObj[inputName] = {
                isValid: true,
                errorMessages: {},
                message: null,
            };
            // check each validation and log an error if no such validation exists
            // ...
        });
        setErrors(errorObj);
    }, [])
    
    // accepts an input name and value for that input, adds or removes errors according to whether or not the value is valid according to the rules initially passed to this hook
    function validate(inputName, value, oldVal) {
        if (!validations[inputName]) {
            console.error(`Cannot validate ${value} for field ${inputName}. No input exists with that name.`)
        } else {
            const newErrors = {...errors};
            Object.keys(validations[inputName]).forEach(validation=>{
                let valid = errorConfigs[validation].callback(value, oldVal);
                if (valid) {
                    delete newErrors[inputName].errorMessages[validation];
                } else {
                    newErrors[inputName].errorMessages[validation] = errorConfigs[validation].message;
                }
                newErrors[inputName].isValid = Object.keys(newErrors[inputName].errorMessages).length === 0;
            })
            setErrors(newErrors);
            return newErrors[inputName];
        }
    }

    return {
        errors: errors,
        validate: validate
    }
}